import type { ResolvedDocument } from "./document/types";

export enum ProposalStatus {
  DRAFT = "draft",
  SENT = "sent",
  DELIVERED = "delivered",
  SIGNED = "signed",
  DECLINED = "declined",
  EXPIRED = "expired",
  VOIDED = "voided",
  BOUNCED = "bounced"
}

export interface ProposalAuthor {
  displayName: string;
  avatarUrl: string;
}

// One recipient's state at the e-sign vendor, as the last scan read it.
export interface RecipientSigningState {
  email: string;
  signed: boolean;
  viewedAt?: string;
  declinedAt?: string;
  // Typed by the signer: plain text.
  declineReason?: string;
}

export interface Proposal {
  id: string;
  name: string;
  status: ProposalStatus;
  // Signers done out of all signers, once it went out.
  signedCount?: number;
  signerCount?: number;
  recipientStates?: RecipientSigningState[];
  createdAt: string;
  updatedAt: string;
  createdBy: ProposalAuthor;
  // The deal or its terms changed after this proposal was built.
  outOfDate?: boolean;
  // What contractsapi allows on it, by name ("save", "rename", "delete");
  // absent on a proposal not stored yet.
  actions?: string[];
}

// Set per slot by the tenant's template. Data slots resolve from CRM records;
// narrative and manual slots are typed in the editor.
export enum ProposalSlotKind {
  DATA = "data",
  NARRATIVE = "narrative",
  MANUAL = "manual"
}

// One spot where a slot's value sits on the composed document, in PDF points
// from the page's top-left corner.
export interface ProposalSlotBox {
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  padding?: number;
  // "Helvetica", "Helvetica-Bold" or "Helvetica-Oblique".
  font?: string;
  // "#rrggbb".
  color?: string;
}

export interface ProposalSlot {
  id: string;
  sectionId: string;
  kind: ProposalSlotKind;
  label: string;
  value: string | null;
  multiline?: boolean;
  // The most characters the rep may type.
  maxLength?: number;
  // Every spot the document shows the value; a name can appear on the cover,
  // in a heading and in the body.
  boxes?: ProposalSlotBox[];
  // The CRM record a data slot resolves from, e.g. "company".
  record?: string;
  // Data the rep can't type over: Terms & Services, or a slot the account locks.
  readOnly?: boolean;
  // Why it can't be typed over, when the backend says, e.g. "Set by Engagement Studio".
  readOnlyNote?: string;
  // False on a field the rep types that may go out blank, e.g. a PO number.
  required?: boolean;
  // The CRM field that would fill a data slot left blank.
  missing?: { record: string; recordId: string | null; field: string };
}

// One line item, keyed by line source and already formatted for display.
export type ProposalLine = Record<string, string | null>;

export interface ProposalSection {
  id: string;
  title: string;
}

export enum ProposalRecipientRole {
  SIGNER = "signer",
  COPY = "copy"
}

// Where one recipient's request stands once the proposal has gone out;
// Delivered means they opened it.
export type ProposalRecipientStatus =
  | ProposalStatus.SENT
  | ProposalStatus.DELIVERED
  | ProposalStatus.SIGNED
  | ProposalStatus.DECLINED
  | ProposalStatus.VOIDED;

export interface ProposalRecipient {
  // The person's id, or the email itself for an address typed by hand.
  key: string;
  personId?: string;
  name: string;
  email: string;
  company?: string;
  jobTitle?: string;
  role: ProposalRecipientRole;
  // Absent until the proposal is sent.
  status?: ProposalRecipientStatus;
  // Set on a recipient who declined.
  declinedAt?: string;
  declineReason?: string;
}

export interface ProposalRecipients {
  // In signing order when `ordered` is set.
  list: ProposalRecipient[];
  ordered: boolean;
}

// The active template's own details, known before any proposal is generated.
export interface ProposalTemplate {
  // A new proposal's default name.
  name: string;
  sections: ProposalSection[];
  // The places the document has for a signature; each takes one signer.
  signaturePlaces: number;
}

// Set on the editor route when a row's Regenerate was confirmed, so the editor
// opens on the rebuilt draft, or when Generate opened it with the recipients
// the rep chose meanwhile.
export interface ProposalEditorLocationState {
  regenerate?: boolean;
  recipients?: ProposalRecipients;
  // Where leaving the proposal returns to, when it wasn't opened from the deal.
  returnTo?: string;
}

// What the rep typed, by slot id.
export type SlotValues = Record<string, string>;

// One stored version of a proposal, draft or not: who it goes to, where each
// of them stands once it went out, and the files it was sent and signed as.
export interface ProposalHistoryVersion {
  id: string;
  status: ProposalStatus;
  createdAt: string;
  updatedAt: string;
  createdBy: ProposalAuthor;
  recipients: ProposalRecipients;
  recipientStates: RecipientSigningState[];
  signedCount?: number;
  signerCount?: number;
  sentFileAttachmentId?: string;
  signedFileAttachmentId?: string;
}

// A Proposals table row: a proposal as its latest version shows it, or one of
// its earlier versions that went out, which only opens to be read.
export interface ProposalRow extends Proposal {
  earlierVersionId?: string;
}

// A stored version, as a save or a Send names the one it is based on.
export interface ProposalVersion {
  id: string;
  lastModifiedAt: string;
}

// The latest version, and the files it went out and came back signed as.
export interface ProposalTracking {
  versionId?: string;
  sentFileAttachmentId?: string;
  signedFileAttachmentId?: string;
}

// What compose reports it could not draw as given: text cut to fit its box,
// and characters the document's font has no glyph for. Send refuses the second.
export interface ComposeFlags {
  cutSlotIds: string[];
  unsupportedSlotIds: string[];
}

export const NO_COMPOSE_FLAGS: ComposeFlags = {
  cutSlotIds: [],
  unsupportedSlotIds: []
};

// What a save stored: the proposal as contractsapi now holds it, and the
// version the next save or Send is based on.
export interface StoredDraft {
  proposal: Proposal;
  version: ProposalVersion;
}

export interface ProposalDocument {
  proposal: Proposal;
  dealId: string;
  sections: ProposalSection[];
  slots: ProposalSlot[];
  // The template version the document is drawn from, and the line items it
  // prints; both are sent back with each compose.
  templateVersionId: string;
  lines: ProposalLine[];
  // The places that template version has for a signature; each takes one signer.
  signaturePlaces: number;
  documentUrl: string;
  // The slots the document at documentUrl could not show as given.
  composedFlags: ComposeFlags;
  // What the rep typed on this proposal, by slot id; never written to the record.
  values: SlotValues;
  // The rep values the document at documentUrl was composed with.
  composedValues: SlotValues;
  // Who signs and who gets a copy; empty until chosen.
  recipients: ProposalRecipients;
  // False until the first Save: a generated proposal is not stored before then.
  saved: boolean;
  // The stored version the editor opened; the next save is based on it.
  version?: ProposalVersion;
  // A flowing template's document, which the editor renders itself; absent for
  // a fixed-box template, shown as its composed PDF.
  resolved?: ResolvedDocument;
}
